import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { loginAndStoreGrampsToken, startOidcLogin } from '../grampsAuth';
import { getDefaultTreeName } from '../treeName';
import { useOidcProviders } from '../hooks/useOidcProviders';

type GrampsAuthDialogProps = {
  open: boolean;
  onAuthenticated: () => void;
};

export function GrampsAuthDialog({ open, onAuthenticated }: GrampsAuthDialogProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const oidcProviders = useOidcProviders();
  const googleProvider = oidcProviders.find((provider) => provider.id === 'google');

  const handleGoogleLogin = () => {
    if (!googleProvider) return;
    startOidcLogin(googleProvider.loginUrl, getDefaultTreeName(), rememberMe ? 'local' : 'session');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const token = await loginAndStoreGrampsToken(
        username,
        password,
        rememberMe ? 'local' : 'session',
      );
      if (!token) {
        setErrorMessage('Connexion impossible. Verifiez votre identifiant et votre mot de passe.');
        return;
      }
      onAuthenticated();
    } catch {
      setErrorMessage('La connexion a echoue. Reessayez dans quelques instants.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(255, 247, 237, 0.55)',
            backdropFilter: 'blur(3px)',
          },
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Connexion a votre arbre familial</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Entrez vos identifiants pour acceder a vos donnees Genealhistory.
            </Typography>
            <TextField
              autoFocus
              label="Identifiant"
              placeholder="Exemple : jerome-..."
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              fullWidth
              required
            />
            <TextField
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              fullWidth
              required
            />
            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />}
              label="Rester connecte sur cet appareil"
            />
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting || !username.trim() || !password}>
            {/* Never swap a bare text node for an element: a browser translation or an
                extension may have replaced that node, and React would fail to remove it. */}
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : <span>Se connecter</span>}
          </Button>
        </DialogActions>
        {googleProvider && <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={2}>
            <Divider>ou</Divider>
            <Button onClick={handleGoogleLogin} variant="outlined" startIcon={<GoogleIcon />} fullWidth>
              Continuer avec Google
            </Button>
          </Stack>
        </DialogContent>}
      </Box>
    </Dialog>
  );
}
